import { useState } from "react";

import { InfoCircleFill } from "@hl/base-components/lib/assets/icons.generated";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { NetworkType } from "@hl/shared-features/lib/apollo/graphql.generated";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks";
import OpenseaRegistryInfoModal from "@hl/shared-features/lib/features/openseaBlocklist/OpenseaBlocklistInfoModal";
import CopyButton from "@hl/shared-features/lib/features/widgets/CopyButton";
import { Divider, Group, Stack, Text, Anchor } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { EditHideCollection } from "~/features/tokens/sections/ManageToken/modals/EditHideCollection/EditHideCollection";
import { PropertyBox } from "~/features/tokens/sections/ManageToken/properties/PropertyBox";

import { _CollectionType } from "../../../../../apollo/graphql.generated";
import { CrossmintNetworks } from "../../../../../config";
import { OpenCrossmintModalButton } from "../modals/CrossmintModal";

export const MoreOptionsProperty = ({
  nonTransferable,
  usingOpenseaBlocklist,
  editionId,
  isDirectMint,
  collectionId,
  creditCardEnabled,
  collectionType,
  isOnchainDutchAuction,
  hasPublicSale,
  network,
  hidden,
  refetch,
}: {
  nonTransferable?: boolean;
  usingOpenseaBlocklist?: boolean | null;
  editionId?: string;
  isDirectMint: boolean;
  collectionId: string;
  creditCardEnabled: boolean;
  collectionType: _CollectionType;
  isOnchainDutchAuction: boolean;
  hasPublicSale: boolean;
  network: NetworkType;
  hidden?: boolean | null;
  refetch: () => void;
}) => {
  const [openseaBlocklistModalOpen, setOpenseaBlocklistModalOpen] =
    useState<boolean>(false);
  const [
    hideCollectionModalOpened,
    { open: openHideCollectionModal, close: closeHideCollectionModal },
  ] = useDisclosure(false);

  // TODO: ff, series or gen series only for now
  const enableSelfServeCrossmint =
    useFeatureFlags(FEATURE_FLAGS.ENABLE_CROSSMINT_SELF_SERVE) &&
    CrossmintNetworks.includes(network) &&
    hasPublicSale;
  const isSeriesBased = [
    _CollectionType.Series,
    _CollectionType.GenerativeSeries,
  ].includes(collectionType);

  return (
    <>
      <PropertyBox
        label="More options"
        value={
          <Stack w="100%">
            {isDirectMint &&
              !isOnchainDutchAuction &&
              enableSelfServeCrossmint && (
                <>
                  <Group position="apart">
                    <Group spacing={7}>
                      <Text size="sm">Enable credit card payments</Text>
                    </Group>
                    <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                      {creditCardEnabled ? "Yes" : "No"} {" · "}
                      <OpenCrossmintModalButton
                        text="Edit"
                        collectionId={collectionId}
                        creditCardEnabled={creditCardEnabled}
                      />
                    </Text>
                  </Group>
                  <Divider />
                </>
              )}

            <Group position="apart">
              <Text size="sm">Make this collection non-transferable</Text>
              <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                {nonTransferable ? "Yes" : "No"}
              </Text>
            </Group>
            {usingOpenseaBlocklist && (
              <>
                <Divider />
                <Group position="apart">
                  <Group spacing={7}>
                    <Text size="sm">
                      Add this token to CORI operator filter registry
                    </Text>
                    <InfoCircleFill
                      width={17}
                      height={17}
                      cursor={"pointer"}
                      onClick={() => setOpenseaBlocklistModalOpen(true)}
                    />
                  </Group>
                  <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                    Yes
                  </Text>
                </Group>
              </>
            )}
            <Divider />
            <Group position="apart">
              <Text size="sm">Collection ID</Text>
              <Group spacing={8}>
                <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                  {collectionId}
                </Text>
                <CopyButton copyValue={collectionId} />
              </Group>
            </Group>
            {(!!editionId || editionId === "0") && !isSeriesBased && (
              <>
                <Divider />
                <Group position="apart">
                  <Text size="sm">Edition ID</Text>
                  <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                    {editionId}
                  </Text>
                </Group>
              </>
            )}
            <Divider />
            <Group position="apart">
              <Text size="sm">Include collection in profile</Text>
              <Group spacing={8}>
                <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                  {!hidden ? "Yes" : "No"} {" · "}
                </Text>
                <Anchor underline={false} onClick={openHideCollectionModal}>
                  <Text size="sm" fw={WEIGHT_BOLD}>
                    Edit
                  </Text>
                </Anchor>
              </Group>
            </Group>
          </Stack>
        }
      />
      <OpenseaRegistryInfoModal
        isOpen={openseaBlocklistModalOpen}
        onClose={() => setOpenseaBlocklistModalOpen(false)}
        onCreateCollectionPage={false}
      />
      <EditHideCollection
        collectionId={collectionId}
        hidden={hidden}
        onClose={closeHideCollectionModal}
        isOpened={hideCollectionModalOpened}
        refetch={refetch}
      />
    </>
  );
};
